import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, TemplateRef, ViewContainerRef, OnDestroy } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

/**
 * Default feedback option shape (no API - parent can override via feedbackOptions input).
 */
export interface ResponseFeedbackOption {
  Id: number | string;
  Name: string;
  IsChecked?: boolean;
}

/**
 * Payload emitted when user submits dislike feedback.
 */
export interface ResponseFeedbackSubmitPayload {
  responseId?: string | number;
  isLike: boolean;
  isDislike: boolean;
  selectedOptionIds: (number | string)[];
  additionalComments: string;
}

/**
 * Shared response feedback component (like / dislike) for use in agents and anywhere else.
 * Renders below timestamp: like/dislike buttons; when dislike is selected, shows feedback form
 * with category options and additional comments. No API calls – parent wires via outputs.
 */
@Component({
  selector: 'app-shared-response-feedback',
  templateUrl: './response-feedback.component.html',
  styleUrls: ['./response-feedback.component.scss']
})
export class SharedResponseFeedbackComponent implements OnChanges, OnDestroy {
  /** Optional id for this response (e.g. messageId / chatHistoryId) for parent to use in events. */
  @Input() responseId: string | number | null = null;

  /** Whether the like button is selected (filled state). */
  @Input() isLikeSelected = false;

  /** Whether the dislike button is selected (filled state). */
  @Input() isDislikeSelected = false;

  /** Whether to show the dislike feedback form (e.g. when user clicks dislike). */
  @Input() showFeedbackForm = false;

  /** Custom feedback options; if not provided, default options are used. */
  @Input() feedbackOptions: ResponseFeedbackOption[] = [];

  /** Additional comments text (two-way or parent-managed). */
  @Input() additionalComments = '';

  /** Disable like/dislike (e.g. while loading). */
  @Input() disabled = false;

  /** When false, hide the like/dislike row (e.g. per-agent config). Default true. */
  @Input() showFeedback = true;

  /** When true, hide checkbox options and allow comment-only feedback submission. */
  @Input() commentsOnly = false;

  @Input() isAgent: boolean = false;

  /** Emitted when user clicks like. Payload: { showFeedbackPopup: true } when isAgent (so parent can show feedback popup). */
  @Output() likeClick = new EventEmitter<{ showFeedbackPopup?: boolean }>();

  /** Emitted when user clicks dislike (toggle – parent may set showFeedbackForm). */
  @Output() dislikeClick = new EventEmitter<void>();

  /** Emitted when user submits the dislike feedback form. */
  @Output() feedbackSubmit = new EventEmitter<ResponseFeedbackSubmitPayload>();

  /** Emitted when user cancels the dislike feedback form. */
  @Output() feedbackCancel = new EventEmitter<void>();

  /** Default options matching chatbot: Incorrect Response, Incorrect References, etc. */
  private readonly defaultFeedbackOptions: ResponseFeedbackOption[] = [
    { Id: 1, Name: 'Incorrect Response', IsChecked: false },
    { Id: 2, Name: 'Incorrect References', IsChecked: false },
    { Id: 3, Name: 'Missing Information', IsChecked: false },
    { Id: 4, Name: 'Irrelevant', IsChecked: false },
    { Id: 5, Name: 'Partially Correct Response', IsChecked: false }
  ];

  get options(): ResponseFeedbackOption[] {
    if (this.feedbackOptions && this.feedbackOptions.length > 0) {
      return this.feedbackOptions;
    }
    return this.defaultFeedbackOptions;
  }

  /** Set when user clicks Submit without selecting any option. */
  showOptionError = false;

 feedbackPopup : boolean = true;

  /** When isAgent: captures whether the feedback popup was opened by like (true) or dislike (false). Used on close so API gets correct isLike even if parent has cleared inputs. */
  private feedbackPopupOpenedByLike: boolean | null = null;

  @ViewChild('feedbackPopupTemplate') feedbackPopupTemplate!: TemplateRef<unknown>;
  private overlayRef: OverlayRef | null = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef
  ) {}

  /** Feedback popup: input value and max length for character count */
  feedbackInputValue: string = '';
   feedbackInputMaxLength: number = 300;

  /** Character count of feedback input (always in sync with feedbackInputValue.length). */
  get feedbackInputCount(): number {
    return this.feedbackInputValue.length;
  }

  /** Thumbs up icon path: when isAgent and selected use like-focus.svg, else default icon-thumbsup.svg. */
  get likeThumbsIcon(): string {
    const base = '../../../assets/icons/';
    if (this.isAgent && this.isLikeSelected) {
      return base + 'like-focus.svg';
    }
    return base + 'icon-thumbsup-1.svg';
  }

  /** Thumbs down icon path: when isAgent and selected use dislike-focus.svg, else default icon-thumbsdown.svg. */
  get dislikeThumbsIcon(): string {
    const base = '../../../assets/icons/';
    if (this.isAgent && this.isDislikeSelected) {
      return base + 'dislike-focus.svg';
    }
    return base + 'icon-thumbsdown-1.svg';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isAgent && changes['showFeedbackForm']) {
      const cur = changes['showFeedbackForm'].currentValue;
      if (cur === true) {
        this.feedbackPopupOpenedByLike = this.isLikeSelected;
        this.openFeedbackOverlay();
      } else {
        this.feedbackPopupOpenedByLike = null;
        this.disposeFeedbackOverlay();
      }
    }
  }

  ngOnDestroy(): void {
    this.disposeFeedbackOverlay();
  }

  /** Open agent feedback popup in a full-screen overlay (attached to body) so it covers the whole screen. */
  private openFeedbackOverlay(): void {
    if (!this.isAgent || !this.feedbackPopupTemplate) return;
    this.disposeFeedbackOverlay();
    const positionStrategy = this.overlay
      .position()
      .global()
      .top('0')
      .left('0')
      .width('100%')
      .height('100%');
    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
    const portal = new TemplatePortal(this.feedbackPopupTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  private disposeFeedbackOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  /** Resolve isLike for agent popup close: use captured value when popup was opened by like/dislike, else fallback to current input. */
  private getIsLikeForEmit(): boolean {
    if (this.isAgent && this.feedbackPopupOpenedByLike !== null) {
      return this.feedbackPopupOpenedByLike;
    }
    return this.isLikeSelected;
  }

  /** Resolve isDislike for agent popup close. */
  private getIsDislikeForEmit(): boolean {
    if (this.isAgent && this.feedbackPopupOpenedByLike !== null) {
      return !this.feedbackPopupOpenedByLike;
    }
    return this.isDislikeSelected;
  }

  onLike(): void {
    if (this.disabled) return;
    if (this.isAgent) {
      // Already liked: toggle off (unlike) – don't show popup, parent will emit and API will send is_like: null
      if (this.isLikeSelected) {
        this.likeClick.emit({ showFeedbackPopup: false });
        return;
      }
      this.feedbackPopupOpenedByLike = true;
      this.likeClick.emit({ showFeedbackPopup: true });
    } else {
      this.likeClick.emit({});
    }
  }

  onDislike(): void {
    if (this.disabled) return;
    this.dislikeClick.emit();
  }

  isAnyOptionChecked(): boolean {
    return this.options.some(opt => opt.IsChecked);
  }

  onFeedbackSubmit(): void {
    if (this.isAgent) {
      const comment = this.feedbackInputValue?.trim() ?? '';
      if (!comment) return;
      this.feedbackSubmit.emit({
        responseId: this.responseId ?? undefined,
        isLike: this.getIsLikeForEmit(),
        isDislike: this.getIsDislikeForEmit(),
        selectedOptionIds: [],
        additionalComments: comment
      });
      this.feedbackInputValue = '';
      return;
    }
    if (!this.commentsOnly) {
      const valid = this.isAnyOptionChecked();
      this.showOptionError = !valid;
      if (!valid) return;
    }
    this.showOptionError = false;
    this.feedbackSubmit.emit({
      responseId: this.responseId ?? undefined,
      isLike: this.isLikeSelected,
      isDislike: this.isDislikeSelected,
      selectedOptionIds: this.commentsOnly ? [] : this.options.filter(o => o.IsChecked).map(o => o.Id),
      additionalComments: this.additionalComments
    });
  }

  /** Skip (agent popup): call API with empty comment, then close. */
  onFeedbackSkip(): void {
    if (this.isAgent) {
      this.feedbackSubmit.emit({
        responseId: this.responseId ?? undefined,
        isLike: this.getIsLikeForEmit(),
        isDislike: this.getIsDislikeForEmit(),
        selectedOptionIds: [],
        additionalComments: ''
      });
      this.feedbackInputValue = '';
      this.feedbackCancel.emit();
      return;
    }
    this.onFeedbackCancel();
  }

  onFeedbackCancel(): void {
    this.feedbackCancel.emit();
  }

  /** Close popup on Escape when keydown is fired on the backdrop. */
  onFeedbackPopupKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onClosFeedbackPopup();
    }
  }

  /** Close (X): same as Skip – call API with empty comment when isAgent, then close (like stays bold). */
  onClosFeedbackPopup(): void {
    if (this.isAgent) {
      this.feedbackSubmit.emit({
        responseId: this.responseId ?? undefined,
        isLike: this.getIsLikeForEmit(),
        isDislike: this.getIsDislikeForEmit(),
        selectedOptionIds: [],
        additionalComments: ''
      });
    }
    this.feedbackInputValue = '';
    this.feedbackCancel.emit();
  }
}
